import { useMemo } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import type { CardId, SquadId } from '@voll-intelligence/domain';

import { useOrgContext } from '@web/ui/core/hooks';

export type CardDetailTabId = 'tasks' | 'conversation' | 'identity' | 'history';
type CardRouteFamily = 'squad' | 'triage';

type BoardView = 'tasks' | 'conversations' | undefined;

/** Objetos aceitos por `navigate(...)` e por `TabBar.Tab` — um formato por família. */
type CardNavigation =
  | {
      to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab';
      params: { orgSlug: string; squadId: SquadId; cardId: CardId; tab: CardDetailTabId };
      search: { view: BoardView };
    }
  | {
      to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab/task/$taskId';
      params: {
        orgSlug: string;
        squadId: SquadId;
        cardId: CardId;
        tab: CardDetailTabId;
        taskId: string;
      };
      search: { view: BoardView };
    }
  | {
      to: '/orgs/$orgSlug/squad/$squadId';
      params: { orgSlug: string; squadId: SquadId };
      search: { view: BoardView };
    }
  | {
      to: '/orgs/$orgSlug/triage/card/$cardId/$tab';
      params: { orgSlug: string; cardId: CardId; tab: CardDetailTabId };
    }
  | {
      to: '/orgs/$orgSlug/triage/card/$cardId/$tab/task/$taskId';
      params: { orgSlug: string; cardId: CardId; tab: CardDetailTabId; taskId: string };
    }
  | { to: '/orgs/$orgSlug/triage'; params: { orgSlug: string }; search?: { autoOpen: false } };

export interface CardRoutes {
  family: CardRouteFamily;
  tab: (tab: CardDetailTabId) => CardNavigation;
  task: (taskId: string, tab?: CardDetailTabId) => CardNavigation;
  /** Voltar/Esc: sai do card para onde ele mora (board ou caixa da triagem). */
  back: CardNavigation;
  /**
   * Depois de transferir: na triagem a caixa segue para o próximo card (sem o
   * `autoOpen: false` do `back`); no squad é o próprio `back`.
   */
  afterTransfer: CardNavigation;
}

/**
 * A família de rotas do card aberto: sob o board de um squad
 * (`/squad/$squadId/card/…`) ou sob a triagem (`/triage/card/…`, cards sem
 * squad). Quem monta URL de aba, tarefa ou "voltar" pergunta aqui em vez de
 * espalhar `squadId` condicional pelas telas. O `view` do board só existe na
 * família squad.
 */
export function useCardRoutes(): CardRoutes {
  const params = useParams({ strict: false });
  const { view } = useSearch({ strict: false });
  const { orgSlug } = useOrgContext();
  const squadId = params.squadId as SquadId | undefined;
  const cardId = params.cardId as CardId;

  return useMemo((): CardRoutes => {
    if (squadId) {
      return {
        family: 'squad',
        tab: (tab) => ({
          to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab',
          params: { orgSlug, squadId, cardId, tab },
          search: { view },
        }),
        task: (taskId, tab = 'tasks') => ({
          to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab/task/$taskId',
          params: { orgSlug, squadId, cardId, tab, taskId },
          search: { view },
        }),
        back: {
          to: '/orgs/$orgSlug/squad/$squadId',
          params: { orgSlug, squadId },
          search: { view },
        },
        afterTransfer: {
          to: '/orgs/$orgSlug/squad/$squadId',
          params: { orgSlug, squadId },
          search: { view },
        },
      };
    }
    return {
      family: 'triage',
      tab: (tab) => ({
        to: '/orgs/$orgSlug/triage/card/$cardId/$tab',
        params: { orgSlug, cardId, tab },
      }),
      task: (taskId, tab = 'tasks') => ({
        to: '/orgs/$orgSlug/triage/card/$cardId/$tab/task/$taskId',
        params: { orgSlug, cardId, tab, taskId },
      }),
      // A `TriagePage` reabre o primeiro card quando nenhum está aberto; sem o
      // `autoOpen: false` o voltar cairia de novo num card — no próprio, quando
      // ele é o topo da lista.
      back: { to: '/orgs/$orgSlug/triage', params: { orgSlug }, search: { autoOpen: false } },
      afterTransfer: { to: '/orgs/$orgSlug/triage', params: { orgSlug } },
    };
  }, [orgSlug, squadId, cardId, view]);
}
